import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setToken as persistToken, getToken } from "../lib/api";
import { usePersonalSocket } from "../hooks/usePersonalSocket";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setTok] = useState(() => getToken());
  const [loading, setLoading] = useState(true);

  const applyToken = useCallback((tok) => {
    persistToken(tok);
    setTok(tok);
  }, []);

  const refreshMe = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/me");
      setUser(data.user);
      return data;
    } catch (e) {
      setUser(null);
      applyToken(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyToken]);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      applyToken(data.token);
      setUser(data.user);
      return data.user;
    },
    [applyToken]
  );

  const register = useCallback(
    async (payload) => {
      const { data } = await api.post("/auth/register", payload);
      applyToken(data.token);
      setUser(data.user);
      return data.user;
    },
    [applyToken]
  );

  const loginWithGoogleSession = useCallback(
    async (sessionId) => {
      const { data } = await api.post("/auth/google", { session_id: sessionId });
      applyToken(data.token);
      setUser(data.user);
      return data.user;
    },
    [applyToken]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) { /* ignore */ }
    applyToken(null);
    setUser(null);
  }, [applyToken]);

  // Phase 5.4: keep a personal WS open whenever we have a valid token + user.
  usePersonalSocket(user && token && !user?.needs_onboarding ? token : null);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      isAuthenticated: !!token && !!user,
      isAdmin: user?.role === "admin",
      needsOnboarding: !!user?.needs_onboarding,
      login,
      register,
      logout,
      loginWithGoogleSession,
      refreshMe,
      setUser,
    }),
    [user, token, loading, login, register, logout, loginWithGoogleSession, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside <AuthProvider>");
  return ctx;
};
